/**
 * Squad — the shared brain.
 *
 * A blackboard holds one agreed estimate of where the player is and how good
 * that estimate is. On top of it sits a **role ticket** system: at any moment
 * only one or two members hold the `aggressor` ticket and are allowed to push,
 * at most one holds `flanker`, at most one holds `grenadier`, and everyone else
 * suppresses or holds their cover. That is the whole difference between a
 * readable Spec-Ops firefight and sixteen men running at you.
 *
 * Tickets carry cooldowns, so the aggressor rotates and the fight breathes.
 *
 * owner: ai
 */
import * as THREE from 'three';
import { dirBin, COVER_DIRS } from './Nav.js';

export const ROLE_HOLD = 'hold';
export const ROLE_SUPPRESS = 'suppress';
export const ROLE_AGGRESSOR = 'aggressor';
export const ROLE_FLANKER = 'flanker';
export const ROLE_GRENADIER = 'grenadier';

const _v = new THREE.Vector3();
const _c = new THREE.Vector3();

export class Squad {
  constructor(id, ctx, rng) {
    this.id = id;
    this.ctx = ctx;
    this.rng = rng;
    this.members = [];

    /* ---- blackboard ---- */
    this.playerPos = new THREE.Vector3();
    this.playerVel = new THREE.Vector3();
    this.confidence = 0;
    this.lastContact = -999;
    this.engaged = false;
    this.centroid = new THREE.Vector3();
    this.threatDir = new THREE.Vector3(0, 0, 1);

    /* ---- tickets ---- */
    this.maxAggressors = 1;
    this.tickets = new Map();          // role -> agent[]
    this.roleSince = new Map();        // agent -> t
    this.nextAggressorAt = 0;
    this.nextFlankAt = 0;
    this.nextGrenadeAt = 0;
    this.calloutCooldown = new Map();  // type -> t

    this.time = 0;
    this.flankGoal = null;             // debug: the last computed flank node
    this.flankAgent = null;
  }

  add(agent) {
    this.members.push(agent);
    agent.squad = this;
    agent.role = ROLE_HOLD;
  }

  remove(agent) {
    const i = this.members.indexOf(agent);
    if (i >= 0) this.members.splice(i, 1);
    if (this.flankAgent === agent) { this.flankAgent = null; this.flankGoal = null; }
  }

  get alive() { return this.members.filter((m) => m.alive); }

  /**
   * Fold one member's belief into the shared one.
   *
   * `lastContact` only moves for a member who can actually see the player —
   * a remembered position is not contact, and the difference is what lets the
   * squad notice it has lost the player and start flushing.
   */
  report(agent, pos, vel, conf, hasLOS) {
    if (hasLOS) this.lastContact = this.time;
    if (conf <= this.confidence * 0.98) return;
    this.playerPos.copy(pos);
    if (vel) this.playerVel.copy(vel);
    this.confidence = conf;
  }

  /* ---------------------------------------------------------------- *
   * the 5 Hz think
   * ---------------------------------------------------------------- */
  think(dt, nav) {
    this.time += dt;
    const alive = this.alive;
    if (alive.length === 0) { this.engaged = false; return; }

    // decay the shared belief
    this.confidence = Math.max(0, this.confidence - 0.06 * dt);

    // strongest individual belief wins
    let best = null, bestConf = 0;
    for (const m of alive) {
      const p = m.perception;
      if (p.confidence > bestConf) { bestConf = p.confidence; best = m; }
    }
    if (best && bestConf > this.confidence) {
      this.playerPos.copy(best.perception.lastKnown);
      this.playerVel.copy(best.perception.lastKnownVel);
      this.confidence = bestConf;
      if (best.perception.hasLOS) this.lastContact = this.time;
    }

    // push the shared belief back down to anyone who knows less
    for (const m of alive) {
      if (m.perception.confidence < this.confidence - 0.25) {
        m.perception.shareFromSquad(this.playerPos, this.confidence, null);
      }
    }

    this.engaged = this.confidence > 0.45;

    // squad centroid + the axis the threat sits on
    this.centroid.set(0, 0, 0);
    for (const m of alive) this.centroid.add(m.pos);
    this.centroid.multiplyScalar(1 / alive.length);
    this.threatDir.copy(this.playerPos).sub(this.centroid);
    this.threatDir.y = 0;
    if (this.threatDir.lengthSq() < 1e-6) this.threatDir.set(0, 0, 1);
    this.threatDir.normalize();

    this.maxAggressors = alive.length >= 5 ? 2 : 1;
    this._assignRoles(alive, nav);
  }

  _assignRoles(alive, nav) {
    const t = this.time;
    if (!this.engaged) {
      for (const m of alive) m.role = ROLE_HOLD;
      this.flankAgent = null; this.flankGoal = null;
      return;
    }

    // --- keep valid ticket holders, drop the dead / the stale ---
    let aggressors = alive.filter((m) => m.role === ROLE_AGGRESSOR);
    let flanker = alive.find((m) => m.role === ROLE_FLANKER) || null;
    const grenadier = alive.find((m) => m.role === ROLE_GRENADIER) || null;

    // rotate the aggressor ticket so the same man does not push forever
    for (const m of aggressors.slice()) {
      const since = t - (this.roleSince.get(m) ?? t);
      if (since > 7.5 || m.health < m.maxHealth * 0.35 || m.suppressedBy > 0.6) {
        m.role = ROLE_SUPPRESS;
        aggressors = aggressors.filter((x) => x !== m);
        this.nextAggressorAt = t + 1.4;
      }
    }
    if (flanker) {
      const since = t - (this.roleSince.get(flanker) ?? t);
      if (since > 16 || flanker.flankDone) { flanker.role = ROLE_SUPPRESS; flanker = null; }
    }

    // --- fill the aggressor ticket ---
    if (aggressors.length < this.maxAggressors && t >= this.nextAggressorAt) {
      const cand = alive
        .filter((m) => m.role !== ROLE_FLANKER && m.role !== ROLE_AGGRESSOR && m.suppressedBy < 0.4)
        .sort((a, b) => a.pos.distanceToSquared(this.playerPos) - b.pos.distanceToSquared(this.playerPos));
      const pick = cand[0];
      if (pick) {
        pick.role = ROLE_AGGRESSOR;
        this.roleSince.set(pick, t);
        aggressors.push(pick);
        pick.wantCallout('moving');
      }
    }

    // --- fill the flanker ticket ---
    if (!flanker && alive.length >= 3 && t >= this.nextFlankAt && nav && nav.ready) {
      const cand = alive
        .filter((m) => m.role !== ROLE_AGGRESSOR && m.suppressedBy < 0.5)
        .sort((a, b) => b.pos.distanceToSquared(this.playerPos) - a.pos.distanceToSquared(this.playerPos));
      for (const m of cand) {
        const goal = this._pickFlankNode(nav, m);
        if (goal >= 0) {
          m.role = ROLE_FLANKER;
          m.flankNode = goal;
          m.flankDone = false;
          this.roleSince.set(m, t);
          this.flankAgent = m;
          this.flankGoal = goal;
          this.nextFlankAt = t + 12;
          m.wantCallout('flanking');
          flanker = m;
          break;
        }
      }
    }

    // --- grenade ticket ---
    if (!grenadier && t >= this.nextGrenadeAt) {
      const cand = alive.find((m) => m.canThrowGrenade());
      if (cand) {
        cand.role = ROLE_GRENADIER;
        this.roleSince.set(cand, t);
        this.nextGrenadeAt = t + 14;
      }
    }

    // --- everyone else suppresses or holds ---
    let suppressors = 0;
    for (const m of alive) {
      if (m.role === ROLE_AGGRESSOR || m.role === ROLE_FLANKER || m.role === ROLE_GRENADIER) continue;
      // roughly two thirds actively suppress, the rest hold and watch angles
      m.role = (suppressors++ % 3 !== 2) ? ROLE_SUPPRESS : ROLE_HOLD;
    }
  }

  /**
   * A flank goal is a cover span that is wide off the squad's own approach
   * axis, still within engagement range of the player, and covered from the
   * player's direction so the flanker survives the trip.
   */
  _pickFlankNode(nav, agent) {
    const P = this.playerPos;
    // which side? alternate so consecutive flanks do not stack on one side
    const side = (this.rng() < 0.5) ? 1 : -1;
    const ax = this.threatDir.x, az = this.threatDir.z;
    const rx = -az * side, rz = ax * side;      // right-hand perpendicular

    let best = -1, bestScore = -Infinity;
    const wantDist = 13;
    nav.forEachCoverNear(P.x, P.z, P.y, 22, (n) => {
      const nx = nav.px[n], ny = nav.py[n], nz = nav.pz[n];
      const dx = nx - P.x, dz = nz - P.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 5.5 || dist > 21) return;
      // the flank node must be off the main axis, on the chosen side
      const lateral = (dx * rx + dz * rz) / dist;
      const along = -(dx * ax + dz * az) / dist;   // +1 = beyond the player
      if (lateral < 0.45) return;
      const cover = nav.coverFrom(n, P.x - nx, P.z - nz);
      if (cover < 1) return;
      const travel = Math.hypot(nx - agent.pos.x, nz - agent.pos.z);
      if (travel > 34) return;
      const score = lateral * 5.0 + cover * 1.6 + along * 1.2
        - Math.abs(dist - wantDist) * 0.16 - travel * 0.06
        - Math.abs(ny - P.y) * 0.35;
      if (score > bestScore) { bestScore = score; best = n; }
    });
    return best;
  }

  /* ---------------------------------------------------------------- *
   * callouts
   * ---------------------------------------------------------------- */

  /** Rate-limited so a squad does not chatter over itself. */
  mayCallout(type, minGap = 4.0) {
    const last = this.calloutCooldown.get(type) ?? -999;
    if (this.time - last < minGap) return false;
    this.calloutCooldown.set(type, this.time);
    return true;
  }

  /** Nearest live mate to `agent`, for "cover me" style directed lines. */
  nearestMate(agent) {
    let best = null, bd = Infinity;
    for (const m of this.members) {
      if (m === agent || !m.alive) continue;
      const d = m.pos.distanceToSquared(agent.pos);
      if (d < bd) { bd = d; best = m; }
    }
    return best;
  }

  /** How many mates currently hold a firing line on the player. */
  countSuppressing() {
    let n = 0;
    for (const m of this.members) if (m.alive && m.role === ROLE_SUPPRESS && m.firing) n++;
    return n;
  }

  /** Is `node` already claimed by another member? */
  isClaimed(node, by) {
    for (const m of this.members) {
      if (m !== by && m.alive && m.coverNode === node) return true;
    }
    return false;
  }

  /**
   * Is (x, z) inside `spacing` metres of a cover span another live member has
   * already committed to, or of where that member is standing right now?
   * Readable tactical spacing is the difference between a squad and a queue.
   */
  tooClose(x, z, by, spacing) {
    const s2 = spacing * spacing;
    for (const m of this.members) {
      if (m === by || !m.alive) continue;
      if (m.coverNode >= 0) {
        const dx = m.coverPos.x - x, dz = m.coverPos.z - z;
        if (dx * dx + dz * dz < s2) return true;
      }
      const ax = m.pos.x - x, az = m.pos.z - z;
      if (ax * ax + az * az < s2 * 0.55) return true;
    }
    return false;
  }

  debugDirBin() {
    return dirBin(this.threatDir.x, this.threatDir.z) % COVER_DIRS;
  }

  reset() {
    this.confidence = 0;
    this.engaged = false;
    this.lastContact = -999;
    this.time = 0;
    this.tickets.clear();
    this.roleSince.clear();
    this.calloutCooldown.clear();
    this.nextAggressorAt = 0;
    this.nextFlankAt = 0;
    this.nextGrenadeAt = 0;
    this.flankAgent = null;
    this.flankGoal = null;
    void _v; void _c;
  }
}
